import React, {useCallback, useMemo} from 'react';
import {SelectGroup} from '@flixbus/honeycomb-react';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {DriverCostCalculationType} from '@/Pages/ProductionCost/store/types';
import {productionCostBaseUpdate} from '@/Pages/ProductionCost/store/slice';
import {DriverCostCalculationTypeSelectorOptions} from './DriverCostCalculationType.constants';
import LabelWithTooltip from '@/components/LabelWithTooltip/LabelWithTooltip';

const DriverCostCalculationTypeSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const driverCostCalculationType = useAppSelector(
    ({productionCost}) => productionCost.driverCostCalculationType
  );
  const driverShiftsCalculationTypeAllowed = useAppSelector(
    ({productionCost}) => productionCost.driverShiftsCalculationTypeAllowed
  );

  const onClickOption = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        productionCostBaseUpdate({
          driverCostCalculationType: event.target
            .value as DriverCostCalculationType
        })
      );
    },
    [dispatch]
  );

  const optionsWithDefaultSelection = useMemo(
    () =>
      DriverCostCalculationTypeSelectorOptions.map(option => ({
        ...option,
        defaultChecked: option.value === driverCostCalculationType,
        disabled:
          option.value === DriverCostCalculationType.DriverShifts &&
          !driverShiftsCalculationTypeAllowed,
        onClick: onClickOption
      })),
    [
      driverCostCalculationType,
      driverShiftsCalculationTypeAllowed,
      onClickOption
    ]
  );

  const selectGroupId = 'driver-cost-calculation-type-select-group';

  return (
    <SelectGroup
      label={
        <LabelWithTooltip
          label="Driver cost calculation logic*"
          htmlFor={selectGroupId}
          tooltipProps={{
            id: `${selectGroupId}-tooltip`,
            content: `
                Here you can select the approach how driver costs are calculated.
                Depending on the chosen approach different input fields will be shown to you.
                Please refer to the CPC documentation for further information.
              `,
            position: 'top'
          }}
        />
      }
      options={optionsWithDefaultSelection}
      id={selectGroupId}
      info={
        driverShiftsCalculationTypeAllowed
          ? null
          : `'Planr Driver Shifts' is not available due to incomplete driver shifts in Planr.`
      }
    />
  );
};

export default React.memo(DriverCostCalculationTypeSelector);
